<template>
  <div class="height-percent-100">
    <div class="display-flex height-percent-100">
      <!-- 左侧租户列表 -->
      <div class="left">
        <div class="display-flex ai-center jc-space-between mb-20">
          <b class="fs-18">一般租户</b>
          <el-button type="text" icon="el-icon-plus" @click="add">添加租户</el-button>
        </div>
        <el-input
          v-model="searchForm.fuzzy"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索租户名称"
          class="mb-20">
        </el-input>
        <div class="list scrollbar-y">
          <el-scrollbar class="height-percent-100">
            <div
              @click="changeTenant(item)"
              class="item"
              :class="{active: selectedTenant && item.id === selectedTenant.id}"
              v-for="item in tenantList"
              :key="item.id">
              <span class="tenant-icon"><i class="el-icon-office-building"></i></span>
              <el-tooltip effect="dark" :content="item.name" placement="top">
                <span class="text-ellipsis fs-14 flex-1 ml-10">{{ item.name }}</span>
              </el-tooltip>
              <!-- 到期提示：一个月内"N天后过期"/当天"今天过期"/已过期"过期N天"/过期超30天"长期过期" -->
              <el-tag
                v-if="item.expiryLabel"
                size="mini"
                :type="expiryTagType(item.expiryLabel)"
                effect="plain"
                class="ml-6 expiry-tag">
                {{ item.expiryLabel }}
              </el-tag>
            </div>
            <el-empty :image-size="100" v-if="!tenantList.length" description="暂无数据"></el-empty>
            <div class="display-flex jc-center mt-20">
              <el-pagination
                hide-on-single-page
                @current-change="(val) => {searchForm.pageNum = val}"
                :current-page="searchForm.pageNum"
                :page-size="searchForm.pageSize"
                layout="prev, pager, next"
                :total="total">
              </el-pagination>
            </div>
          </el-scrollbar>
        </div>
      </div>

      <!-- 右侧租户详情 -->
      <div class="right scrollbar-y">
        <template v-if="selectedTenant">
          <div class="display-flex ai-center jc-space-between">
            <div class="display-flex ai-center">
              <b class="fs-16">已选择：{{ selectedTenant.name }}</b>
              <el-tag size="mini" class="ml-10">一般租户</el-tag>
            </div>
            <div>
              <el-button type="text" icon="el-icon-document-copy" @click="copyLoginInfo">复制登录信息</el-button>
              <el-button type="text" class="color-success" icon="el-icon-magic-stick" @click="initDialog.show = true">初始化租户</el-button>
              <el-button type="text" icon="el-icon-edit-outline" @click="edit(selectedTenant)">编辑租户</el-button>
              <!-- 仅已过有效期的租户允许删除，未过期时禁用 -->
              <el-button type="text" class="color-danger" icon="el-icon-delete" :disabled="!canDeleteTenant" @click="del(selectedTenant)">删除租户</el-button>
            </div>
          </div>
          <el-divider class="mt-16 mb-20"></el-divider>

          <div class="product-card" v-for="item in sysList" :key="item.id">
            <div class="attr-grid">
              <!-- 基础属性 -->
              <section>
                <b class="section-title">基础属性</b>
                <div class="attr-row">
                  <span class="attr-label">创建日期</span>
                  <span class="attr-value">{{ item.createAt ? $moment(item.createAt).format('YYYY-MM-DD') : '-' }}</span>
                </div>
                <div class="attr-row">
                  <span class="attr-label">上次启用</span>
                  <span class="attr-value">{{ item.enableDate ? $moment(item.enableDate).format('YYYY-MM-DD') : '-' }}</span>
                </div>
                <div class="attr-row">
                  <span class="attr-label"><i class="req">*</i>有效期</span>
                  <span class="attr-value">{{ item.expDate ? $moment(item.expDate).format('YYYY-MM-DD') : '-' }}</span>
                </div>
                <div class="attr-row">
                  <span class="attr-label"><i class="req">*</i>系统状态</span>
                  <span class="attr-value">
                    <el-tag size="small" :type="item.state === 1 ? 'success' : 'info'">{{ item.state === 1 ? '已启用' : '已禁用' }}</el-tag>
                  </span>
                </div>
                <div class="attr-row">
                  <span class="attr-label"><i class="req">*</i>超管账号</span>
                  <span class="attr-value">
                    {{ item.username || '-' }}
                  </span>
                </div>
                <div class="attr-row">
                  <span class="attr-label">消息接收人
                    <el-tooltip effect="dark" content="管理人员自动接收全部消息，此处选择额外接收消息的人员。" placement="top">
                      <i class="el-icon-question cursor-pointer"></i>
                    </el-tooltip>
                  </span>
                  <span class="attr-value">{{ receiverText(item) }}</span>
                </div>
                <div class="attr-row">
                  <span class="attr-label">经度</span>
                  <span class="attr-value">{{ item.longitude || '-' }}</span>
                </div>
                <div class="attr-row">
                  <span class="attr-label">纬度</span>
                  <span class="attr-value">{{ item.latitude || '-' }}</span>
                </div>
              </section>
              <!-- 运行与资源配置 -->
              <section>
                <b class="section-title">运行与资源配置</b>
                <div class="attr-row">
                  <span class="attr-label">是否独立部署</span>
                  <span class="attr-value">
                    <el-tag size="mini" :type="item.independentDeployFlag === 1 ? 'warning' : 'info'" effect="plain">
                      {{ item.independentDeployFlag === 1 ? '是' : '否' }}
                    </el-tag>
                  </span>
                </div>
                <!-- 独立部署时才展示服务域名 -->
                <div class="attr-row" v-if="item.independentDeployFlag === 1">
                  <span class="attr-label">租户服务域名</span>
                  <span class="attr-value">{{ item.serviceDomain || '-' }}</span>
                </div>
                <div class="attr-row">
                  <span class="attr-label">账户数</span>
                  <span class="attr-value">
                    {{ item.accountSizeUsed || 0 }}/{{ item.accountSize || 0 }}
                  </span>
                </div>
                <div class="attr-row" v-if="item.yhAi === 1">
                  <span class="attr-label">AI识别数</span>
                  <span class="attr-value">{{ item.userAiCount || 0 }}/{{ item.aiCount || 0 }}</span>
                </div>
                <div class="attr-row" v-if="item.yhAi === 1">
                  <span class="attr-label">高级AI</span>
                  <span class="attr-value">{{ item.openAiList && item.openAiList.length ? item.openAiList.join('、') : '-' }}</span>
                </div>
                <div class="attr-row">
                  <span class="attr-label">短信数</span>
                  <span class="attr-value">{{ item.smsUseCount || 0 }}/{{ item.smsCount || 0 }}</span>
                </div>
              </section>
            </div>
          </div>
          <el-empty :image-size="100" v-if="!sysList.length" description="该租户暂无开通的系统"></el-empty>
        </template>
        <el-empty v-else description="请选择左侧租户"></el-empty>
      </div>
    </div>

    <!-- 添加/编辑租户（整体表单） -->
    <el-dialog
      :title="formDialog.mode === 'add' ? '添加一般租户' : '编辑租户'"
      :visible.sync="formDialog.show"
      width="640px"
      append-to-body>
      <el-form label-position="top" class="diy-top-form">
        <b class="section-title">基础属性</b>
        <el-form-item label="名称" required>
          <div class="display-flex width-percent-100">
            <el-input
              v-model="formDialog.form.name"
              style="flex: 1"
              maxlength="50"
              show-word-limit
              placeholder="请输入租户名称"
              @input="handleTenantNameInput">
            </el-input>
            <el-button class="ml-10" icon="el-icon-location-outline" @click="matchGeo('edit')">匹配经纬度</el-button>
          </div>
        </el-form-item>
        <div class="display-flex width-percent-100">
          <el-form-item label="有效期" required style="flex: 1" class="mr-20">
            <el-date-picker
              v-model="formDialog.form.expDate"
              type="date"
              value-format="yyyy-MM-dd"
              :clearable="false"
              placeholder="请选择有效期"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>
          <el-form-item label="系统状态" style="flex: 1">
            <div class="display-flex ai-center" style="height: 40px">
              <span class="mr-10 fs-14" :class="{ 'color-primary': !formDialog.form.enabled }">禁用</span>
              <el-switch v-model="formDialog.form.enabled"></el-switch>
              <span class="ml-10 fs-14" :class="{ 'color-primary': formDialog.form.enabled }">启用</span>
            </div>
          </el-form-item>
        </div>
        <div class="display-flex width-percent-100">
          <el-form-item label="超管账号" required style="flex: 1" class="mr-20">
            <el-input v-model="formDialog.form.username" maxlength="50" placeholder="请输入超管账号"></el-input>
          </el-form-item>
          <el-form-item style="flex: 1" label=" ">
          </el-form-item>
        </div>
        <el-form-item label="消息接收人">
          <span slot="label">消息接收人
            <el-tooltip effect="dark" content="管理人员自动接收全部消息，此处选择额外接收消息的人员。" placement="top">
              <i class="el-icon-question color-info cursor-pointer"></i>
            </el-tooltip>
          </span>
          <limit-select
            v-model="formDialog.form.msgReceiverIds"
            :options="receiverOptions"
            placeholder="请选择消息接收人（最多 10 人）"
            @change="handleReceiverChange">
          </limit-select>
        </el-form-item>
        <div class="display-flex width-percent-100">
          <el-form-item label="经度" style="flex: 1" class="mr-20">
            <el-input v-model="formDialog.form.longitude" maxlength="20" placeholder="请输入经度"></el-input>
          </el-form-item>
          <el-form-item label="纬度" style="flex: 1">
            <el-input v-model="formDialog.form.latitude" maxlength="20" placeholder="请输入纬度"></el-input>
          </el-form-item>
        </div>

        <b class="section-title mt-10">运行与资源配置</b>
        <el-form-item label="是否独立部署" class="mb-10">
          <div class="display-flex ai-center" style="height: 32px">
            <el-switch v-model="formDialog.form.independentDeploy" active-text="是" inactive-text="否"></el-switch>
          </div>
        </el-form-item>
        <!-- 独立部署时服务域名为必填项，未独立部署时不需要填写，单独占一行 -->
        <el-form-item v-if="formDialog.form.independentDeploy" label="租户服务域名（询问研发）" required>
          <el-input
            v-model="formDialog.form.serviceDomain"
            maxlength="100"
            show-word-limit
            placeholder="请输入服务域名">
          </el-input>
        </el-form-item>
        <div class="display-flex width-percent-100">
          <el-form-item label="账户数上限" required style="flex: 1" class="mr-20">
            <el-input-number
              v-model="formDialog.form.accountSize"
              :min="0"
              :max="100000"
              controls-position="right"
              style="width: 100%">
            </el-input-number>
          </el-form-item>
          <el-form-item v-if="formDialog.mode === 'add' || formDialog.yhAi" label="AI识别数上限" required style="flex: 1">
            <el-input-number
              v-model="formDialog.form.aiCount"
              :min="0"
              controls-position="right"
              style="width: 100%">
            </el-input-number>
          </el-form-item>
        </div>
        <el-form-item v-if="formDialog.mode === 'add' || formDialog.yhAi" label="高级AI">
          <el-checkbox-group v-model="formDialog.form.openAiList">
            <el-checkbox label="培训"></el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="短信数上限" required style="width: calc(50% - 10px)">
          <el-input-number
            v-model="formDialog.form.smsCount"
            :min="0"
            controls-position="right"
            style="width: 100%">
          </el-input-number>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="formDialog.show = false">取 消</el-button>
        <el-button type="primary" :loading="formDialog.loading" @click="submitForm">{{ formDialog.mode === 'add' ? '确 定' : '保 存' }}</el-button>
      </span>
    </el-dialog>

    <!-- 初始化租户 -->
    <init-tenant-dialog
      :visible.sync="initDialog.show"
      :tenant-id="selectedTenant ? selectedTenant.id : null"
      :tenant-name="selectedTenant ? selectedTenant.name : ''">
    </init-tenant-dialog>

    <!-- 匹配经纬度 -->
    <el-dialog
      title="匹配经纬度"
      :visible.sync="geoDialog.show"
      width="560px"
      append-to-body>
      <p class="fs-14 margin-0 mb-16">根据租户名称匹配到以下候选点位： <b>{{ geoDialog.name }}</b></p>
      <div class="geo-list" v-loading="geoDialog.loading">
        <div
          v-for="(item, index) in geoDialog.list"
          :key="index"
          class="geo-item"
          :class="{ active: geoDialog.selected === index }"
          @click="geoDialog.selected = index">
          <div style="flex: 1">
            <b class="fs-14">{{ item.name }}</b>
            <p class="geo-address margin-0 mt-10">
              <i class="el-icon-location-outline color-primary mr-6"></i>{{ item.address }}
            </p>
            <p class="geo-coord margin-0 mt-6">经度 {{ item.longitude }} · 纬度 {{ item.latitude }}</p>
          </div>
          <el-radio v-model="geoDialog.selected" :label="index"><span></span></el-radio>
        </div>
        <el-empty
          v-if="!geoDialog.loading && !geoDialog.list.length"
          :image-size="80"
          description="未匹配到相关点位，请手动输入经纬度">
        </el-empty>
      </div>
      <span slot="footer">
        <el-button @click="geoDialog.show = false">取 消</el-button>
        <el-button type="primary" :disabled="geoDialog.selected === null" @click="useGeoPoint">使用此点位</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import InitTenantDialog from './InitTenantDialog.vue'
import LimitSelect from '@/components/LimitSelect.vue'
export default {
  name: 'Index', // 一般租户
  components: { InitTenantDialog, LimitSelect },
  props: {},
  data () {
    return {
      total: 0,
      searchForm: {
        pageNum: 1,
        pageSize: 10,
        fuzzy: ''
      },
      selectedTenant: null, // 选择的租户
      tenantList: [], // 租户列表
      sysList: [], // 租户开通的系统列表
      // 添加/编辑租户弹框（整体表单）
      formDialog: {
        show: false,
        loading: false,
        mode: 'add', // add 添加 / edit 编辑
        yhAi: false,
        product: null,
        form: {
          name: '',
          username: '',
          msgReceiverIds: [],
          expDate: '',
          enabled: true,
          independentDeploy: false,
          serviceDomain: '',
          accountSize: 0,
          aiCount: 0,
          smsCount: 0,
          openAiList: [],
          longitude: '',
          latitude: ''
        }
      },
      // 消息接收人候选人员（/sys/user/options，只加载一次）
      receiverOptions: [],
      receiversLoaded: false,
      // 初始化租户弹框（接口暂未接入，仅 UI 占位）
      initDialog: {
        show: false
      },
      // 匹配经纬度弹框
      geoTarget: 'edit', // edit=编辑弹框 panel=详情面板
      geoDialog: {
        show: false,
        loading: false,
        name: '',
        list: [],
        selected: null
      }
    }
  },
  filters: {},
  watch: {
    _searchForm: {
      handler: function (newVal, oldVal) {
        if (newVal.pageNum !== 1 && newVal.pageNum === oldVal.pageNum) {
          this.searchForm.pageNum = 1
        } else {
          this.getTenantList()
        }
      },
      deep: true
    }
  },
  computed: {
    /**
     * 监听复杂数据类型的变换
     */
    _searchForm () {
      return JSON.parse(JSON.stringify(this.searchForm))
    },
    /**
     * 仅已过有效期的租户允许删除，避免误删仍在服务期内的租户
     */
    canDeleteTenant () {
      if (!this.selectedTenant) return false
      const expDate = this.formatExpDate(this.selectedTenant.expDate)
      if (!expDate) return false
      return expDate < this.$moment().format('YYYY-MM-DD')
    }
  },
  methods: {
    /**
     * 消息接收人展示（/tenant/product/list 返回的 msgReceiverList 为解析后的人员简要信息）
     */
    receiverText (product) {
      const list = product && Array.isArray(product.msgReceiverList) ? product.msgReceiverList : []
      const names = list.map(item => item.name).filter(Boolean)
      return names.length ? names.join('、') : '-'
    },
    /**
     * 切换租户
     */
    changeTenant (val) {
      this.selectedTenant = val
      this.getSystemList()
    },
    /**
     * 添加租户：打开整体表单弹框
     */
    add () {
      this.openFormDialog('add')
    },
    /**
     * 列表上的编辑：定位租户后打开整体表单弹框（第一个系统）
     */
    edit (item) {
      this.selectedTenant = item
      this.getSystemList(() => {
        if (!this.sysList.length) {
          this.$message.warning('该租户暂无开通的系统，无法编辑配置')
          return
        }
        this.openFormDialog('edit', this.sysList[0])
      })
    },
    /**
     * 名称联动超管账号：添加模式下名称变化时超管账号始终跟随（单向，改账号不回写名称）
     */
    handleTenantNameInput (val) {
      if (this.formDialog.mode === 'add') {
        this.formDialog.form.username = val
      }
    },
    /**
     * 打开添加/编辑租户弹框（名称/经纬度/消息接收人/独立部署走租户接口，其余走系统配置接口）
     */
    openFormDialog (mode, product) {
      this.formDialog.mode = mode
      this.formDialog.product = product || null
      this.formDialog.yhAi = !!(product && product.yhAi === 1)
      if (mode === 'add') {
        this.formDialog.form = {
          name: '',
          username: '',
          msgReceiverIds: [],
          // 默认有效期：当前日期之后的一个月
          expDate: this.$moment().add(1, 'months').format('YYYY-MM-DD'),
          enabled: true,
          independentDeploy: false,
          serviceDomain: '',
          accountSize: 10,
          aiCount: 1000,
          smsCount: 100,
          openAiList: ['培训'], // 默认勾选高级AI-培训
          longitude: '',
          latitude: ''
        }
      } else {
        const item = product
        // 租户级字段（接收人/独立部署/经纬度）从 /tenant/product/list 返回中取，字段名一致
        const receivers = Array.isArray(item.msgReceiverList) ? item.msgReceiverList : []
        this.formDialog.form = {
          name: this.selectedTenant.name || '',
          username: item.username || '',
          msgReceiverIds: receivers.map(r => r.id).filter(id => id !== null && id !== undefined),
          expDate: this.formatExpDate(item.expDate),
          enabled: item.state === 1,
          independentDeploy: item.independentDeployFlag === 1,
          serviceDomain: item.serviceDomain || '',
          accountSize: item.accountSize || 0,
          aiCount: item.aiCount || 0,
          smsCount: item.smsCount || 0,
          openAiList: item.openAiList ? [...item.openAiList] : [],
          longitude: item.longitude || '',
          latitude: item.latitude || ''
        }
      }
      this.formDialog.show = true
      // 已选接收人可能不在候选里（如已停用人员），合并进候选保证回显标签正确
      this.loadReceivers(mode === 'edit' && product
        ? (Array.isArray(product.msgReceiverList) ? product.msgReceiverList : [])
          .map(r => { return { id: r.id, name: r.name } })
          .filter(r => r.id !== null && r.id !== undefined)
        : [])
    },
    /**
     * 消息接收人候选人员：GET /sys/user/options（全量 id+姓名，只加载一次）
     */
    loadReceivers (extra) {
      const merge = (list) => {
        const extras = (extra || []).filter(e => e.id && !list.some(o => o.id === e.id))
        this.receiverOptions = list.concat(extras)
      }
      if (this.receiversLoaded) {
        merge(this.receiverOptions)
        return
      }
      this.$api.organization.userOptions().then(res => {
        this.receiversLoaded = true
        const list = (res || []).map(item => {
          return { id: item.id, name: item.name }
        })
        merge(list)
      }).catch(() => {})
    },
    /**
     * 消息接收人最多 10 人
     */
    handleReceiverChange (val) {
      if (val.length > 10) {
        this.$message.warning('消息接收人最多选择 10 人')
        this.formDialog.form.msgReceiverIds = val.slice(0, 10)
      }
    },
    /**
     * 表单校验（添加/编辑共用）
     */
    validateForm () {
      const form = this.formDialog.form
      if (!form.name.trim()) {
        this.$message.warning('请输入租户名称')
        return false
      }
      if (!form.expDate) {
        this.$message.warning('请选择有效期')
        return false
      }
      if (!form.username.trim()) {
        this.$message.warning('请输入超管账号')
        return false
      }
      // 独立部署时服务域名为必填项，未独立部署时不需要填写
      if (form.independentDeploy && !form.serviceDomain.trim()) {
        this.$message.warning('独立部署时请填写服务域名')
        return false
      }
      const showAi = this.formDialog.mode === 'add' || this.formDialog.yhAi
      if (form.accountSize === null || form.accountSize === undefined ||
        form.smsCount === null || form.smsCount === undefined ||
        (showAi && (form.aiCount === null || form.aiCount === undefined))) {
        this.$message.warning('请填写配额上限')
        return false
      }
      return true
    },
    /**
     * 保存（按模式分发）
     */
    submitForm () {
      if (!this.validateForm()) return
      if (this.formDialog.mode === 'add') {
        this.submitAdd()
      } else {
        this.submitEdit()
      }
    },
    /**
     * 添加租户：先校验重名（假删公司可重启），再整体提交
     */
    submitAdd () {
      const name = this.formDialog.form.name.trim()
      this.formDialog.loading = true
      this.$api.project.checkTenant({ name }).then(res => {
        if (res === null) {
          this.doAddTenant()
        } else if (res.delFlag === 1) {
          this.formDialog.loading = false
          this.$confirm('是否重启原假删公司?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }).then(() => {
            this.formDialog.loading = true
            this.doAddTenant()
          }).catch(() => {})
        } else if (res.delFlag === 0) {
          this.formDialog.loading = false
          this.$message.warning('已有相同名称企业')
        } else {
          this.formDialog.loading = false
        }
      }).catch(() => {
        this.formDialog.loading = false
      })
    },
    /**
     * 执行添加租户（整体字段，走 /tenant/save）
     */
    doAddTenant () {
      const form = this.formDialog.form
      const data = {
        name: form.name.trim(),
        remark: '',
        username: form.username.trim(),
        expDate: this.formatExpDate(form.expDate),
        state: form.enabled ? 1 : 2,
        accountSize: form.accountSize,
        aiCount: form.aiCount,
        smsCount: form.smsCount,
        // 高级AI按后端约定传 '[0]'（0=培训）或空字符串
        openAi: form.openAiList.includes('培训') ? '[0]' : '',
        // 消息接收人：sys_user.id 数组，最多 10 人
        msgReceivers: form.msgReceiverIds,
        independentDeployFlag: form.independentDeploy ? 1 : 0
      }
      // 独立部署的租户必填服务域名，留空则不推送同步
      if (form.independentDeploy) data.serviceDomain = form.serviceDomain.trim()
      const longitude = String(form.longitude || '').trim()
      const latitude = String(form.latitude || '').trim()
      if (longitude) data.longitude = longitude
      if (latitude) data.latitude = latitude
      this.$api.project.addTenant(data).then(() => {
        this.formDialog.loading = false
        this.$message.success('添加成功')
        this.formDialog.show = false
        this.searchForm.pageNum = 1
        this.getTenantList()
      }).catch(() => {
        this.formDialog.loading = false
      })
    },
    /**
     * 保存编辑（整体修改）
     */
    submitEdit () {
      const form = this.formDialog.form
      const product = this.formDialog.product
      this.formDialog.loading = true
      // 租户整体字段（与新增接口字段一致：username/expDate/state/配额等必传）
      const tenantData = {
        id: this.selectedTenant.id,
        name: form.name.trim(),
        remark: '',
        username: form.username.trim(),
        expDate: this.formatExpDate(form.expDate),
        state: form.enabled ? 1 : 2,
        accountSize: form.accountSize,
        aiCount: form.aiCount,
        smsCount: form.smsCount,
        // 高级AI按后端约定传 '[0]'（0=培训）或空字符串
        openAi: form.openAiList.includes('培训') ? '[0]' : '',
        // 消息接收人：sys_user.id 数组，最多 10 人
        msgReceivers: form.msgReceiverIds,
        independentDeployFlag: form.independentDeploy ? 1 : 0,
        serviceDomain: form.independentDeploy ? form.serviceDomain.trim() : ''
      }
      const longitude = String(form.longitude || '').trim()
      const latitude = String(form.latitude || '').trim()
      if (longitude) tenantData.longitude = longitude
      if (latitude) tenantData.latitude = latitude
      // 系统配置
      const systemData = {
        accountSize: form.accountSize,
        expDate: this.formatExpDate(form.expDate),
        productId: product.productId,
        state: form.enabled ? 1 : 2,
        tenantId: product.tenantId,
        username: form.username.trim(),
        aiCount: form.aiCount,
        smsCount: form.smsCount,
        // 高级AI按后端约定传 '[0]'（0=培训）或空字符串
        openAi: form.openAiList.includes('培训') ? '[0]' : ''
      }
      this.$api.project.updateTenant(tenantData).then(() => {
        return this.$api.project.systemUpdate(systemData)
      }).then(() => {
        this.formDialog.loading = false
        this.$message.success('保存成功')
        this.formDialog.show = false
        this.selectedTenant.name = form.name.trim()
        // 租户级字段展示在 /tenant/product/list 的数据上，本地同步回写避免闪烁
        product.longitude = longitude
        product.latitude = latitude
        product.independentDeployFlag = form.independentDeploy ? 1 : 0
        product.serviceDomain = tenantData.serviceDomain
        product.msgReceiverList = form.msgReceiverIds.map(id => {
          const found = this.receiverOptions.find(r => r.id === id)
          return found ? { id: found.id, name: found.name } : { id }
        })
        this.getTenantList()
        this.getSystemList()
      }).catch(() => {
        this.formDialog.loading = false
      })
    },
    /**
     * 到期提示徽标颜色："N天后过期"为提醒色，其余（今天过期/过期N天/长期过期）为危险色
     */
    expiryTagType (label) {
      if (!label) return 'info'
      return label.includes('天后过期') ? 'warning' : 'danger'
    },
    /**
     * 复制登录信息：GET /tenant/loginInfo（登录地址/超管账号/默认密码，一般租户与集团通用）
     */
    copyLoginInfo () {
      if (!this.selectedTenant) {
        this.$message.warning('请先选择租户')
        return
      }
      this.$api.project.loginInfo({ tenantId: this.selectedTenant.id }).then(res => {
        const info = res || {}
        const text = `登录地址：${info.loginUrl || '-'} 超管账号：${info.adminAccount || '-'} 默认密码：${info.defaultPassword || '-'}`
        if (navigator.clipboard) {
          navigator.clipboard.writeText(text).catch(() => {})
        }
        this.$message({ type: 'info', message: text, duration: 5000 })
      })
    },
    /**
     * 删除租户：仅已过有效期的租户允许删除，避免误删仍在服务期内的租户
     */
    del (item) {
      // 按租户有效期判断，仅已过期才允许删除
      const today = this.$moment().format('YYYY-MM-DD')
      const expDate = this.formatExpDate(item.expDate)
      if (!expDate || expDate >= today) {
        this.$message.warning('仅已过有效期的租户允许删除，避免误删仍在服务期内的租户')
        return
      }
      this.$confirm(`仅已过有效期的租户允许删除。确认删除「${item.name}」吗？`, '删除租户', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.$api.project.delTenant([item.id]).then(res => {
          this.$message.success('删除成功')
          this.selectedTenant = null
          this.sysList = []
          this.searchForm.pageNum = 1
          this.getTenantList()
        })
      }).catch(() => {})
    },
    /**
     * 租户分页列表
     */
    getTenantList: _.debounce(function () {
      this.$api.project.tenantList(this.searchForm).then(res => {
        this.tenantList = res.list || []
        this.total = res.total
      }).catch(() => {})
    }, 500),
    /**
     * 租户系统列表
     */
    getSystemList (callback) {
      if (!this.selectedTenant) return
      this.$api.project.systemList({ tenantId: this.selectedTenant.id }).then(res => {
        this.sysList = (res || []).map(item => {
          return {
            ...item,
            openAiList: this.parseOpenAiList(item.openAiList)
          }
        })
        if (callback) callback()
      })
    },
    /**
     * 高级AI配置归一化：接口可能返回 '[0]' 字符串或 ["[0]"] 嵌套数组（0 表示培训），统一转成 ['培训']
     */
    parseOpenAiList (val) {
      if (!val) return []
      let parsed = val
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed)
        } catch (e) {
          parsed = parsed.split(',').filter(Boolean)
        }
      }
      if (!Array.isArray(parsed)) return []
      // 后端可能把 '[0]' 整个字符串再包进数组，如 ["[0]"]，需要展开
      const flat = []
      parsed.forEach(item => {
        if (typeof item === 'string' && /^\[.*\]$/.test(item)) {
          try {
            flat.push(...JSON.parse(item))
          } catch (e) {
            flat.push(item)
          }
        } else {
          flat.push(item)
        }
      })
      return flat
        .map(item => (item === 0 || item === '0') ? '培训' : item)
        .filter(item => item !== '' && item !== null && item !== undefined)
    },
    /**
     * 有效期格式化为 yyyy-MM-dd
     */
    formatExpDate (val) {
      if (!val) return ''
      if (val instanceof Date) {
        const y = val.getFullYear()
        const m = String(val.getMonth() + 1).padStart(2, '0')
        const d = String(val.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      const match = String(val).match(/\d{4}-\d{2}-\d{2}/)
      return match ? match[0] : ''
    },
    /**
     * JSONP 请求（腾讯位置服务 WebService 不支持 CORS）
     */
    jsonp (url) {
      return new Promise((resolve, reject) => {
        const callback = `__qqMapCb${Date.now()}`
        const script = document.createElement('script')
        const cleanup = () => {
          delete window[callback]
          document.body.removeChild(script)
        }
        window[callback] = (data) => {
          cleanup()
          resolve(data)
        }
        script.onerror = () => {
          cleanup()
          reject(new Error('jsonp error'))
        }
        script.src = `${url}&output=jsonp&callback=${callback}`
        document.body.appendChild(script)
      })
    },
    /**
     * 根据名称匹配经纬度：调用腾讯位置服务关键词输入提示接口，按名称检索候选点位
     * key 配置在 .env 文件的 VUE_APP_TX_KEY
     */
    matchGeo (from) {
      this.geoTarget = from || 'edit'
      const name = this.geoTarget === 'panel'
        ? (this.selectedTenant ? this.selectedTenant.name : '')
        : this.formDialog.form.name.trim()
      if (!name) {
        this.$message.warning('请先填写名称')
        return
      }
      const key = process.env.VUE_APP_TX_KEY
      if (!key) {
        this.$message.warning('未配置腾讯位置服务 key（VUE_APP_TX_KEY）')
        return
      }
      this.geoDialog.name = name
      this.geoDialog.selected = null
      this.geoDialog.list = []
      this.geoDialog.loading = true
      this.geoDialog.show = true
      const url = 'https://apis.map.qq.com/ws/place/v1/suggestion' +
        `?keyword=${encodeURIComponent(name)}` +
        `&key=${key}`
      this.jsonp(url).then(res => {
        this.geoDialog.loading = false
        if (res.status === 0) {
          // 关键词提示返回候选点位列表，部分结果可能没有坐标，过滤掉
          this.geoDialog.list = (res.data || []).filter(item => item.location).map(item => {
            return {
              name: item.title,
              address: item.address || '',
              longitude: Number(item.location.lng).toFixed(6),
              latitude: Number(item.location.lat).toFixed(6)
            }
          })
          if (!this.geoDialog.list.length) {
            this.$message.warning('未匹配到该名称对应的点位')
          }
        } else {
          this.$message.warning(res.message || '点位匹配失败')
        }
      }).catch(() => {
        this.geoDialog.loading = false
        this.$message.warning('点位匹配失败，请检查网络后重试，或手动输入经纬度')
      })
    },
    /**
     * 使用此点位：编辑弹框里回填表单；详情面板上直接保存到租户
     */
    useGeoPoint () {
      const point = this.geoDialog.list[this.geoDialog.selected]
      if (!point) return
      if (this.geoTarget === 'panel') {
        const data = {
          id: this.selectedTenant.id,
          name: this.selectedTenant.name,
          remark: '',
          longitude: point.longitude,
          latitude: point.latitude
        }
        this.$api.project.updateTenant(data).then(() => {
          this.$message.success('经纬度已更新')
          // 经纬度展示在 /tenant/product/list 的数据上
          if (this.sysList[0]) {
            this.sysList[0].longitude = point.longitude
            this.sysList[0].latitude = point.latitude
          }
          this.getTenantList()
        })
      } else {
        this.formDialog.form.longitude = point.longitude
        this.formDialog.form.latitude = point.latitude
      }
      this.geoDialog.show = false
    }
  },
  beforeCreate () {
  },
  created () {
  },
  beforeMount () {
  },
  mounted () {
    this.getTenantList()
  },
  beforeUpdate () {
  },
  updated () {
  },
  beforeDestroy () {
  },
  destroyed () {
  }
}
</script>

<style lang="scss" scoped>
.left {
  width: 340px;
  padding: 20px;
  margin-right: 16px;
  background-color: white;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  .list {
    height: 0;
    flex: 1;
    box-sizing: border-box;
    font-size: 14px;
    .item {
      padding: 14px 16px;
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      border: 1px solid #E6E6E6;
      border-radius: 4px;
      cursor: pointer;
      &:last-child {
        margin-bottom: 0;
      }
      .tenant-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        flex: 0 0 28px;
        border-radius: 4px;
        color: #fff;
        background: #3377FF;
        font-size: 16px;
      }
      .expiry-tag {
        flex-shrink: 0;
      }
    }
    .active {
      border: 1px solid #328FFF;
      position: relative;
      &:before {
        text-align: right;
        font-size: 12px;
        padding: 2px;
        content: '√';
        position: absolute;
        right: 0;
        top: 0;
        width: 14px;
        height: 14px;
        background: #1774FF;
        color: #ffffff;
        border-bottom-left-radius: 20px;
      }
    }
  }
}
.right {
  flex: 1;
  padding: 20px;
  // 内容超出时显示纵向滚动条
  overflow-y: auto;
  overflow-x: hidden;
  background-color: #FFFFFF;
  .product-card {
    border: 1px solid #EBEEF5;
    border-radius: 4px;
    padding: 20px;
    margin-bottom: 16px;
  }
  .attr-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 40px;
  }
}
.section-title {
  display: block;
  font-size: 15px;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 3px solid #3377FF;
  line-height: 1.2;
}
.attr-row {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 6px 0;
  border-bottom: 1px solid #F2F4F7;
  font-size: 13px;
  &:last-child {
    border-bottom: 0;
  }
  .attr-label {
    flex: 0 0 110px;
    color: #909399;
    .req {
      color: #F56C6C;
      font-style: normal;
      margin-right: 2px;
    }
  }
  .attr-value {
    flex: 1;
    display: flex;
    align-items: center;
    color: #303133;
    min-width: 0;
  }
}
.geo-list {
  max-height: 400px;
  overflow-y: auto;
  .geo-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    padding: 14px 16px;
    margin-bottom: 12px;
    cursor: pointer;
    &.active {
      border-color: #409EFF;
      background-color: #ecf5ff;
    }
    .geo-address {
      font-size: 13px;
      color: #606266;
    }
    .geo-coord {
      font-size: 12px;
      color: #909399;
    }
  }
}
</style>
